import { useState } from 'react';

interface UserAvatarProps {
  /** ユーザーの表示名 */
  displayName?: string | null;
  /** ユーザーのメールアドレス（displayNameがない場合のフォールバック） */
  email?: string | null;
  /** アバター画像のURL */
  avatarUrl?: string | null;
  /** アバターのサイズ（Tailwind CSSクラス） */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** カスタムクラス名 */
  className?: string;
  /** 小さいスクリーンで非表示にするモード（Profileヘッダーなどで使用） */
  hideOnSmall?: boolean;
}

/**
 * ユーザーアバターコンポーネント
 *
 * 優先順位:
 * 1. avatarUrl があれば画像を表示
 * 2. なければ displayName または email の先頭文字を使った丸文字を表示
 */
export default function UserAvatar({
  displayName,
  email,
  avatarUrl,
  size = 'md',
  className = '',
  hideOnSmall = false,
}: UserAvatarProps) {
  const [imgError, setImgError] = useState(false);

  // サイズマッピング
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-xl',
  };

  // 表示する先頭文字を取得
  const getInitial = () => {
    if (displayName) return displayName.charAt(0).toUpperCase();
    if (email) return email.charAt(0).toUpperCase();
    return 'U';
  };

  const sizeClass = sizeClasses[size];

  const hiddenClass = hideOnSmall ? 'hidden' : '';

  if (avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        alt={displayName || email || 'User'}
        className={`${hiddenClass} ${hideOnSmall ? 'sm:block' : ''} ${sizeClass} rounded-full object-cover ${className} flex-shrink-0`}
        referrerPolicy="no-referrer"
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      className={`${hiddenClass} ${hideOnSmall ? 'sm:grid' : 'grid'} ${sizeClass} rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 place-items-center text-white font-semibold leading-none text-center overflow-hidden ${className} flex-shrink-0`}
    >
      <span className="inline-block leading-none">{getInitial()}</span>
    </div>
  );
}
